import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import useAutoRefresh from '@/hooks/useAutoRefresh';
import {
  ClipboardList, Calendar, Check, Clock, Users, BookOpen,
  RefreshCw, ArrowRight, AlertCircle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatCard = ({ icon: Icon, color, label, value, sub }) => (
  <div className="bg-white rounded-xl p-5 border border-slate-200">
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 ${color.bg} rounded-lg flex items-center justify-center`}>
        <Icon className={`w-5 h-5 ${color.text}`} />
      </div>
      <div>
        <p className="text-sm text-slate-500">{label}</p>
        <p className="text-xl font-bold text-slate-900">{value}</p>
        {sub && <p className="text-xs text-slate-400">{sub}</p>}
      </div>
    </div>
  </div>
);

const AdminExamsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/exams-overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      console.error('Failed to load exams overview', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  useAutoRefresh(() => fetchOverview());

  const summary = data?.summary || {};
  const courses = data?.courses || [];
  const recent = data?.recent_attempts || [];

  return (
    <div className="space-y-6" data-testid="admin-exams-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Exams Management</h1>
          <p className="text-slate-500 mt-1">Live overview of courses, question bank readiness & student exam attempts</p>
        </div>
        <button
          onClick={fetchOverview}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
          data-testid="refresh-exams-overview"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <StatCard
          icon={BookOpen}
          color={{ bg: 'bg-blue-100', text: 'text-blue-600' }}
          label="Professional Courses"
          value={summary.total_courses ?? 0}
          sub={`${summary.courses_ready ?? 0} ready (≥45 Q)`}
        />
        <StatCard
          icon={ClipboardList}
          color={{ bg: 'bg-amber-100', text: 'text-amber-600' }}
          label="Total Questions"
          value={summary.total_questions ?? 0}
          sub="across all courses"
        />
        <StatCard
          icon={Users}
          color={{ bg: 'bg-emerald-100', text: 'text-emerald-600' }}
          label="Exam Attempts"
          value={summary.total_attempts ?? 0}
          sub={`${summary.completed_attempts ?? 0} completed`}
        />
        <StatCard
          icon={Check}
          color={{ bg: 'bg-purple-100', text: 'text-purple-600' }}
          label="Pass Rate"
          value={`${summary.pass_rate ?? 0}%`}
          sub="of completed"
        />
      </div>

      {/* Courses table — real data */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-slate-900">Course Question-Bank Status</h3>
            <p className="text-xs text-slate-500">Auto-synced with /admin/courses (Single Source of Truth)</p>
          </div>
          <Link
            to="/admin/question-bank"
            className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
          >
            Manage Question Bank <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        {loading ? (
          <div className="p-8 text-center text-slate-500">Loading…</div>
        ) : courses.length === 0 ? (
          <div className="p-8 text-center text-slate-500 flex flex-col items-center gap-2">
            <AlertCircle className="w-6 h-6 text-slate-400" />
            No professional courses found. Add one in /admin/courses.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Course</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Questions</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-slate-600">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {courses.map((c) => (
                <tr key={c.id} className="hover:bg-slate-50" data-testid={`exam-course-row-${c.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-900">{c.name}</td>
                  <td className="px-4 py-3 text-slate-700 font-semibold">{c.questions}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      c.ready_for_exam ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}>
                      {c.ready_for_exam ? 'Ready' : `Need ${Math.max(0, 45 - c.questions)} more`}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/question-bank?course=${c.id}`}
                      className="text-blue-600 hover:underline text-sm"
                      data-testid={`view-questions-${c.id}`}
                    >
                      View Questions
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Recent attempts */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h3 className="font-semibold text-slate-900">Recent Exam Attempts</h3>
          <p className="text-xs text-slate-500">Most recent 10 across online & dynamic exams</p>
        </div>
        {recent.length === 0 ? (
          <div className="p-8 text-center text-slate-500 flex flex-col items-center gap-2">
            <Calendar className="w-6 h-6 text-slate-400" />
            No exam attempts yet.
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Student</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Course</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Score</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Status</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-slate-600">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {recent.map((r, idx) => {
                const isPass = ['pass', 'passed'].includes((r.status || '').toLowerCase());
                return (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{r.student}</td>
                    <td className="px-4 py-3 text-slate-600">{r.course}</td>
                    <td className="px-4 py-3 font-semibold">{Math.round(Number(r.score) || 0)}%</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${
                        isPass ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {r.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500">{r.date || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AdminExamsPage;
